import React from 'react';
// NOTE: Use @sentry/react for React-specific features like ErrorBoundary
// @sentry/capacitor (used in sentryConfig.ts for initialization) does NOT export ErrorBoundary
// These packages work together:
// - @sentry/capacitor: Initialization, platform detection (web/iOS/Android), native crash reporting
// - @sentry/react: React-specific features (ErrorBoundary, withProfiler, React error handling)
import * as Sentry from '@sentry/react';
import { Box, Button, Heading, Text, VStack } from '@chakra-ui/react';
import { colors, fontStyles, spacing } from '../../styles';

interface FallbackProps {
  error: Error;
  componentStack: string | null;
  eventId: string | null;
  resetError: () => void;
}

const ErrorFallback: React.FC<FallbackProps> = ({ error, componentStack, eventId, resetError }) => {
  const isDev = import.meta.env.DEV;

  return (
    <Box minH="100vh" display="flex" alignItems="center" justifyContent="center" bg={colors.sugar} p={spacing.deka}>
      <VStack spacing={spacing.deci} maxW="500px" textAlign="center">
        <Heading css={fontStyles.rhino} color={colors.petrolBlue}>
          Something went wrong
        </Heading>

        <Text css={fontStyles.cat} color={colors.tungsten}>
          {isDev ? error.message : 'We encountered an unexpected error. Our team has been notified.'}
        </Text>

        {isDev && componentStack && (
          <Box
            as="pre"
            p={spacing.deci}
            bg={colors.modes.light.statusBar}
            borderRadius="8px"
            textAlign="left"
            fontSize="12px"
            maxH="300px"
            overflow="auto"
            w="100%"
          >
            <code>{componentStack}</code>
          </Box>
        )}

        {eventId && (
          <Text css={fontStyles.mouse} color={colors.tungsten}>
            Error ID: {eventId}
          </Text>
        )}

        <Button onClick={resetError} colorScheme="blue" size="lg" mt={spacing.deci}>
          Try Again
        </Button>

        {isDev && (
          <Button onClick={() => window.location.reload()} variant="ghost" size="sm">
            Reload Page
          </Button>
        )}
      </VStack>
    </Box>
  );
};

interface ErrorBoundaryProps {
  children: React.ReactNode;
  showDialog?: boolean;
  onReset?: () => void;
}

export const AppErrorBoundary: React.FC<ErrorBoundaryProps> = ({ children, showDialog = false, onReset }) => {
  return (
    <Sentry.ErrorBoundary
      fallback={({ error, componentStack, eventId, resetError }) => (
        <ErrorFallback
          error={error instanceof Error ? error : new Error(String(error))}
          componentStack={componentStack}
          eventId={eventId}
          resetError={resetError}
        />
      )}
      showDialog={showDialog && import.meta.env.PROD}
      onReset={onReset}
      beforeCapture={(scope) => {
        scope.setTag('error_boundary', 'true');
      }}
    >
      {children}
    </Sentry.ErrorBoundary>
  );
};
